import React, { useState, useEffect } from "react";
import styles from "./Config.module.css";

let debounceTimeout;

const Config = ({ settingsOptionSelected, setSettingsOptionSelected, font }) => {
	const apiUrl = process.env.REACT_APP_API_URL;
  	const token = localStorage.getItem("authToken");
    const [expiration, setExpiration] = useState("");
  	const [isInfinite, setIsInfinite] = useState(false);
  	const [shopifyOrderNoteActive, setShopifyOrderNoteActive] = useState(true);
  	const [packiyoExchangeTrigger, setPackiyoExchangeTrigger] = useState("delivered");
  	const [packiyoEnabled, setPackiyoEnabled] = useState(false);

  	//fetch when exchanges get written into packiyo
  	useEffect(() => {
		const fetchPackiyoExchangeTrigger = async () => {
			try {
				const response = await fetch(`${apiUrl}/api/fetch_packiyo_exchange_trigger/`, {
					method: "GET",
					headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
				});
				const data = await response.json();
				setPackiyoExchangeTrigger(data.packiyo_exchange_trigger);
				setPackiyoEnabled(data.packiyo_enabled);
			} catch (err) { console.error("Failed to fetch packiyo exchange trigger:", err); }
		};
		fetchPackiyoExchangeTrigger();
	}, []);

	const handlePackiyoTriggerChange = async (trigger) => {
		if (trigger === packiyoExchangeTrigger) return;
		try {
			const response = await fetch(`${apiUrl}/api/update_packiyo_exchange_trigger/`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
				body: JSON.stringify({ packiyo_exchange_trigger: trigger }),
			});
			const data = await response.json();
			if (response.ok) setPackiyoExchangeTrigger(data.packiyo_exchange_trigger);
		} catch (err) { console.error("Failed to update packiyo exchange trigger:", err); }
	};

  	//fetch shopify order note toggle state
  	useEffect(() => {
		const fetchOrderNoteState = async () => {
			try {
				const response = await fetch(`${apiUrl}/api/fetch_shopify_order_note_active/`, {
					method: "GET",
					headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
				});
				const data = await response.json();
				setShopifyOrderNoteActive(data.shopify_order_note_active);
			} catch (err) { console.error("Failed to fetch order note state:", err); }
		};
		fetchOrderNoteState();
	}, []);

	const handleOrderNoteToggle = async () => {
		try {
			const response = await fetch(`${apiUrl}/api/toggle_shopify_order_note_active/`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
			});
			const data = await response.json();
			setShopifyOrderNoteActive(data.shopify_order_note_active);
		} catch (err) { console.error("Failed to toggle order note:", err); }
	};

  	//fetch link expiration policy
  	useEffect(() => {
		const fetchExpirationPolicy = async () => {
		    try {
		      const response = await fetch(`${apiUrl}/api/fetch_expiration_policy/`, {
		      	method: "GET",
		        headers: {
		        	"Content-Type": "application/json",
		          	Authorization: `Bearer ${token}`,
		        },
		      });
		      const data = await response.json();
		      if (data.expiration_days === null) {
		        setIsInfinite(true);
		        setExpiration("No expiration");
		      } else {
		      	setIsInfinite(false);
		        setExpiration(String(data.expiration_days));
		      }
		    } catch (err) { console.error("Failed to fetch expiration policy:", err); }
  		};
  		fetchExpirationPolicy();
	}, []);

  	//prevent rapid concurrent db updates as user types
  	useEffect(() => {
	    if (!isInfinite && expiration !== "") {
	      clearTimeout(debounceTimeout);
	      debounceTimeout = setTimeout(() => { updateExpirationPolicy(expiration); }, 500);
	    }
	    return () => clearTimeout(debounceTimeout);
  	}, [expiration, isInfinite]);

	//update model field in db
	const updateExpirationPolicy = async (value) => {
	    try {
	    	const response = fetch(`${apiUrl}/api/update_expiration_policy/`, {
		        method: "POST",
		        headers: {
		        	"Content-Type": "application/json",
		          	Authorization: `Bearer ${token}`
		        },
		        body: JSON.stringify({ expiration_days: value === "No expiration" || isInfinite ? null : Number(value) })
	      	});
	      	const data = await response;
	    } catch (err) { console.error("Failed to update expiration policy:", err); }
	};

	const handleChange = (e) => {
    	const val = e.target.value;
    	if (/^\d*$/.test(val)) { setExpiration(val); }
  	};

  	const handleToggleInfinity = () => {
	    const newState = !isInfinite;
	    setIsInfinite(newState);
	    const newExpiration = newState ? "No expiration" : "";
	    setExpiration(newExpiration);
	    updateExpirationPolicy(newExpiration);
  	};

  return (
    <div className={`${styles.config_container} ${font} fade_in`}>
    	<p className={`${styles.config_settings_title} titles`}>Config settings</p>
      	<div className={`${font} ${styles.config_wrapper}`}>
	        <div className={styles.config_card}>

	          	{/* ── Row 1: Expiration label spans both grid columns ── */}
	          	<label className={`${styles.config_label} ${styles.label_full}`}>
	          		Expiration Policy <span className={styles.config_hint}>(days until orders can no longer be returned.)</span>
	          	</label>

	          	{/* ── Row 2: col-1 spacer keeps controls in col 2 ── */}
	          	<div />
	            <div className={styles.input_group}>
	            	<input className={styles.config_input} type="text" value={expiration} onChange={handleChange} disabled={isInfinite} placeholder={"Enter number of days"}/>
	              	{!isInfinite && (<span className={styles.unit_label}>days</span>)}
	              	<button className={`${styles.infinity_btn} ${isInfinite ? styles.highlight_no_expiration : ""}`} onClick={handleToggleInfinity}>&#x221E;</button>
	          	</div>

	          	{/* ── Row 3: Shopify label (col 1) + toggle right-aligned under &#x221E; (col 2) ── */}
	          	<label className={styles.config_label}>Shopify Order Note</label>
	          	<div className={styles.toggle_right}>
	          		<img
	          			className={styles.order_note_toggle}
	          			src={`${process.env.PUBLIC_URL}/images/${shopifyOrderNoteActive ? "toggle_on.png" : "toggle_off.png"}`}
	          			alt="shopify order note toggle"
	          			onClick={handleOrderNoteToggle}
	          		/>
	          	</div>

	          	{/* ── Row 4: Packiyo exchange trigger (only for businesses with packiyo integration) ── */}
	          	{packiyoEnabled && (
	          		<>
	          			<label className={styles.config_label}>
	          				Write Exchanges to Packiyo <span className={styles.config_hint}>(when return is)</span>
	          			</label>
	          			<div className={styles.segmented_control}>
	          				{[["in_transit", "In transit"], ["delivered", "Delivered"]].map(([value, label]) => (
	          					<button
	          						key={value}
	          						className={`${styles.segment_btn} ${packiyoExchangeTrigger === value ? styles.segment_selected : ""}`}
	          						onClick={() => handlePackiyoTriggerChange(value)}
	          					>
	          						{label}
	          					</button>
	          				))}
	          			</div>
	          		</>
	          	)}

	        </div>
      	</div>
      	<button onClick={() => setSettingsOptionSelected("settings_back")} className={`back_buttons ${styles.config_back_button}`}>
			<img src={`${process.env.PUBLIC_URL}/images/back_button_arrow.png`} className={styles.config_back_button_arrow} alt="back"/>
		</button>
    </div>
  );
};

export default Config;
